import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Filter, Eye, Building2, MapPin, Globe,
  RefreshCw, X, FileText, BarChart3, Users, Clock, Image as ImageIcon, Radio,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../api/client';
import { DataTable } from '../../../components/ui/DataTable';
import { Modal } from '../../../components/ui/Modal';
import { type ColumnDef } from '@tanstack/react-table';

interface AuditoriaComunicado {
  id: string;
  titulo: string;
  corpo: string;
  tipo: string;
  categoria: string;
  destinatario: string;
  status: string;
  fixado: boolean;
  criadoEm: string;
  configPreview?: {
    midiaUrl?: string;
    arquivoUrl?: string;
    midiaTitulo?: string;
    midiaId?: string;
    tags?: string[];
    isMasterBroadcast?: boolean;
    destinatarios?: string[];
  };
  escola?: {
    id: string;
    nome: string;
    codigo?: string;
    _count?: { alunos: number; turmas: number };
    municipio?: {
      id: string;
      nome: string;
      estado?: { id: string; nome: string; sigla: string; rede?: { id: string; nome: string } };
    };
  };
  municipio?: {
    id: string;
    nome: string;
    estado?: { id: string; nome: string; sigla: string; rede?: { id: string; nome: string } };
  };
  turma?: { id: string; nome: string; _count?: { alunos: number } };
  criadoPor?: { id: string; nome: string; email: string; perfil: string };
  _count?: { visualizacoes: number };
}

const TIPO_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  AVISO:    { bg: '#fef3c7', color: '#d97706', border: '#fcd34d' },
  URGENTE:  { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  EVENTO:   { bg: '#f3e8ff', color: '#7c3aed', border: '#d8b4fe' },
  REUNIAO:  { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  LEMBRETE: { bg: '#dbeafe', color: '#2563eb', border: '#93c5fd' },
};

const filterSelectStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1.5px solid #cbd5e1',
  background: '#ffffff',
  color: '#0f172a',
  fontSize: '.82rem',
  fontWeight: 500,
  outline: 'none',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

export function MasterAuditoriaTab() {
  const [items, setItems] = useState<AuditoriaComunicado[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filtros Hierárquicos
  const [redes, setRedes] = useState<{ id: string; nome: string }[]>([]);
  const [estados, setEstados] = useState<{ id: string; nome: string; sigla: string; redeId?: string }[]>([]);
  const [municipios, setMunicipios] = useState<{ id: string; nome: string; estadoId?: string }[]>([]);
  const [escolas, setEscolas] = useState<{ id: string; nome: string; municipioId?: string }[]>([]);

  const [selectedRede, setSelectedRede] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');
  const [selectedMunicipio, setSelectedMunicipio] = useState('');
  const [selectedEscola, setSelectedEscola] = useState('');
  const [selectedTipo, setSelectedTipo] = useState('');
  const [search, setSearch] = useState('');

  // Modal Detalhes Completo
  const [detailItem, setDetailItem] = useState<AuditoriaComunicado | null>(null);

  // Carregar listas de hierarquia
  useEffect(() => {
    void api.get('/admin/redes').then((res) => setRedes(res.data || [])).catch((err) => console.warn('[MasterAuditoriaTab] Erro ao carregar redes:', err));
    void api.get('/admin/estados').then((res) => setEstados(res.data || [])).catch((err) => console.warn('[MasterAuditoriaTab] Erro ao carregar estados:', err));
    void api.get('/admin/municipios').then((res) => setMunicipios(res.data || [])).catch((err) => console.warn('[MasterAuditoriaTab] Erro ao carregar municípios:', err));
    void api.get('/escolas').then((res) => setEscolas(res.data || [])).catch((err) => console.warn('[MasterAuditoriaTab] Erro ao carregar escolas:', err));
  }, []);

  // Filtragem dinâmica de selects em cascata
  const filteredEstados = useMemo(() => {
    if (!selectedRede) return estados;
    return estados.filter((e) => e.redeId === selectedRede);
  }, [estados, selectedRede]);

  const filteredMunicipios = useMemo(() => {
    if (!selectedEstado) return municipios;
    return municipios.filter((m) => m.estadoId === selectedEstado);
  }, [municipios, selectedEstado]);

  const filteredEscolas = useMemo(() => {
    if (!selectedMunicipio) return escolas;
    return escolas.filter((e) => e.municipioId === selectedMunicipio);
  }, [escolas, selectedMunicipio]);

  const fetchAuditoria = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedRede) params.set('redeId', selectedRede);
      if (selectedEstado) params.set('estadoId', selectedEstado);
      if (selectedMunicipio) params.set('municipioId', selectedMunicipio);
      if (selectedEscola) params.set('escolaId', selectedEscola);
      if (selectedTipo) params.set('tipo', selectedTipo);
      if (search.trim()) params.set('search', search.trim());
      params.set('page', String(page));
      params.set('limit', String(pageSize));

      const { data } = await api.get(`/master/comunicados/auditoria?${params.toString()}`);
      setItems(data.data || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Erro ao consultar auditoria de comunicados');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedRede, selectedEstado, selectedMunicipio, selectedEscola, selectedTipo, search, page, pageSize]);

  useEffect(() => {
    fetchAuditoria();
  }, [fetchAuditoria]);

  const clearFilters = () => {
    setSelectedRede('');
    setSelectedEstado('');
    setSelectedMunicipio('');
    setSelectedEscola('');
    setSelectedTipo('');
    setSearch('');
    setPage(1);
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const columns: ColumnDef<AuditoriaComunicado>[] = [
    {
      accessorKey: 'titulo',
      header: 'Título / Conteúdo',
      cell: ({ row }) => {
        const c = row.original;
        const hasMedia = Boolean(c.configPreview?.midiaUrl || c.configPreview?.arquivoUrl);
        return (
          <div style={{ maxWidth: 280 }}>
            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              {hasMedia && (
                <span title="Contém anexo/mídia" style={{ display: 'inline-flex', flexShrink: 0 }}>
                  <ImageIcon size={13} style={{ color: '#2563eb' }} />
                </span>
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.titulo}</span>
            </div>
            <div style={{
              fontSize: '.75rem', color: '#64748b', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {c.corpo}
            </div>
          </div>
        );
      },
    },
    {
      id: 'origem',
      header: 'Origem do Envio',
      cell: ({ row }) => {
        const c = row.original;
        if (c.escola) {
          return (
            <div>
              <div style={{ fontWeight: 600, fontSize: '.82rem', color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Building2 size={12} /> {c.escola.nome}
              </div>
              <div style={{ fontSize: '.72rem', color: '#64748b' }}>
                {c.escola.municipio?.nome} - {c.escola.municipio?.estado?.sigla}
              </div>
            </div>
          );
        }
        if (c.municipio) {
          return (
            <div>
              <div style={{ fontWeight: 600, fontSize: '.82rem', color: '#047857', display: 'flex', alignItems: 'center', gap: 4 }}>
                <MapPin size={12} /> Pref. {c.municipio.nome}
              </div>
              <div style={{ fontSize: '.72rem', color: '#64748b' }}>
                Secretaria Municipal ({c.municipio.estado?.sigla})
              </div>
            </div>
          );
        }
        return (
          <span style={{ fontSize: '.78rem', color: '#6366f1', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Radio size={12} /> Plataforma Master
          </span>
        );
      },
    },
    {
      accessorKey: 'tipo',
      header: 'Tipo',
      cell: ({ row }) => {
        const c = TIPO_COLORS[row.original.tipo] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
        return (
          <span style={{
            padding: '3px 8px', borderRadius: 6, fontSize: '.7rem', fontWeight: 700,
            background: c.bg, color: c.color, border: `1px solid ${c.border}`, textTransform: 'uppercase',
          }}>
            {row.original.tipo}
          </span>
        );
      },
    },
    {
      accessorKey: 'destinatario',
      header: 'Destinatário',
      cell: ({ row }) => {
        const d = row.original.destinatario;
        return (
          <span style={{
            padding: '2px 8px', borderRadius: 12, fontSize: '.72rem', fontWeight: 600,
            background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0',
          }}>
            {d === 'TODOS' ? 'Todos' : d === 'TURMA' ? `Turma ${row.original.turma?.nome || ''}` : d}
          </span>
        );
      },
    },
    {
      id: 'visualizacoes',
      header: 'Visualizações',
      cell: ({ row }) => (
        <span style={{ fontSize: '.8rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Eye size={13} style={{ color: '#059669' }} />
          {row.original._count?.visualizacoes || 0}
        </span>
      ),
    },
    {
      accessorKey: 'criadoEm',
      header: 'Data de Envio',
      cell: ({ row }) => (
        <span style={{ fontSize: '.78rem', color: '#64748b' }}>
          {formatDate(row.original.criadoEm)}
        </span>
      ),
    },
    {
      id: 'acoes',
      header: 'Ações',
      cell: ({ row }) => (
        <button
          onClick={() => setDetailItem(row.original)}
          style={{
            padding: '5px 10px', borderRadius: 6, border: '1.5px solid #cbd5e1',
            background: '#ffffff', cursor: 'pointer', fontSize: '.75rem',
            color: '#2563eb', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
          }}
          title="Ver auditoria completa"
        >
          <Eye size={13} /> Detalhes
        </button>
      ),
    },
  ];

  const hasDetailMedia = Boolean(detailItem?.configPreview?.midiaUrl || detailItem?.configPreview?.arquivoUrl);
  const mediaUrl = detailItem?.configPreview?.midiaUrl || detailItem?.configPreview?.arquivoUrl || '';
  const isImage = /\.(jpeg|jpg|gif|png|webp|svg)($|\?)/i.test(mediaUrl) || mediaUrl.includes('image') || mediaUrl.includes('WhatsApp');
  const isVideo = /\.(mp4|webm|ogg|mov)($|\?)/i.test(mediaUrl) || mediaUrl.includes('video');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Barra de Filtros em Cascata ── */}
      <div style={{
        padding: '14px 18px', background: '#ffffff', borderRadius: 12,
        border: '1.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: '.88rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Filter size={15} style={{ color: '#2563eb' }} /> Filtros da Rede Nacional
          </span>
          {(selectedRede || selectedEstado || selectedMunicipio || selectedEscola || selectedTipo || search) && (
            <button
              onClick={clearFilters}
              style={{
                background: 'none', border: 'none', color: '#dc2626', fontSize: '.78rem',
                fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <X size={12} /> Limpar Filtros
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
          {/* Rede */}
          <select
            value={selectedRede}
            onChange={(e) => {
              setSelectedRede(e.target.value);
              setSelectedEstado('');
              setSelectedMunicipio('');
              setSelectedEscola('');
              setPage(1);
            }}
            style={filterSelectStyle}
          >
            <option value="">Todas as Redes</option>
            {redes.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
          </select>

          {/* Estado */}
          <select
            value={selectedEstado}
            onChange={(e) => {
              setSelectedEstado(e.target.value);
              setSelectedMunicipio('');
              setSelectedEscola('');
              setPage(1);
            }}
            style={filterSelectStyle}
          >
            <option value="">Todos os Estados</option>
            {filteredEstados.map((e) => <option key={e.id} value={e.id}>{e.nome} ({e.sigla})</option>)}
          </select>

          {/* Município */}
          <select
            value={selectedMunicipio}
            onChange={(e) => {
              setSelectedMunicipio(e.target.value);
              setSelectedEscola('');
              setPage(1);
            }}
            style={filterSelectStyle}
          >
            <option value="">Todos os Municípios</option>
            {filteredMunicipios.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
          </select>

          {/* Escola */}
          <select
            value={selectedEscola}
            onChange={(e) => { setSelectedEscola(e.target.value); setPage(1); }}
            style={filterSelectStyle}
          >
            <option value="">Todas as Escolas</option>
            {filteredEscolas.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>

          {/* Tipo */}
          <select
            value={selectedTipo}
            onChange={(e) => { setSelectedTipo(e.target.value); setPage(1); }}
            style={filterSelectStyle}
          >
            <option value="">Todos os Tipos</option>
            <option value="AVISO">Aviso</option>
            <option value="URGENTE">Urgente</option>
            <option value="EVENTO">Evento</option>
            <option value="REUNIAO">Reunião</option>
            <option value="LEMBRETE">Lembrete</option>
          </select>

          {/* Busca Textual */}
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar título..."
              style={{
                width: '100%', padding: '8px 12px 8px 30px', borderRadius: 8,
                border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                fontSize: '.82rem', outline: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Tabela de Auditoria com Paginação Única Integrada ── */}
      <div style={{
        background: '#ffffff', borderRadius: 12,
        border: '1px solid #e2e8f0', overflow: 'hidden',
      }}>
        <DataTable
          columns={columns}
          data={items}
          isLoading={loading}
          searchPlaceholder=""
          showToolbar={false}
          pageSize={pageSize}
          serverPagination={{
            pageIndex: page - 1,
            pageCount: Math.ceil(total / pageSize) || 1,
            totalItems: total,
            onPageChange: (p) => setPage(p + 1),
            pageSizeOptions: [15, 25, 50, 100],
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(1);
            },
          }}
        />
      </div>

      {/* ── Modal de Detalhes Completos do Comunicado (Layout 2 Colunas com Mídia) ── */}
      {detailItem && (
        <Modal
          open={!!detailItem}
          onClose={() => setDetailItem(null)}
          title={`Auditoria de Comunicado: ${detailItem.titulo}`}
          size={hasDetailMedia ? 'wide' : 'lg'}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 20,
            alignItems: 'flex-start',
          }}>
            {/* ═══ COLUNA ESQUERDA: DADOS, HIERARQUIA E TEXTO ═══ */}
            <div style={{
              flex: hasDetailMedia ? '1.1 1 420px' : '1 1 100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}>
              {/* ── Bloco 1: Hierarquia Geográfica e Emissor ── */}
              <div style={{
                padding: 14, background: '#f8fafc', borderRadius: 10,
                border: '1.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ fontSize: '.75rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Globe size={13} style={{ color: '#2563eb' }} /> Hierarquia & Localização de Origem
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                  <div>
                    <span style={{ fontSize: '.72rem', color: '#64748b', display: 'block' }}>Rede de Ensino:</span>
                    <strong style={{ fontSize: '.84rem', color: '#0f172a' }}>
                      {detailItem.escola?.municipio?.estado?.rede?.nome || detailItem.municipio?.estado?.rede?.nome || 'Rede Geral'}
                    </strong>
                  </div>

                  <div>
                    <span style={{ fontSize: '.72rem', color: '#64748b', display: 'block' }}>Estado / UF:</span>
                    <strong style={{ fontSize: '.84rem', color: '#0f172a' }}>
                      {detailItem.escola?.municipio?.estado?.nome || detailItem.municipio?.estado?.nome || 'Nacional'} ({detailItem.escola?.municipio?.estado?.sigla || detailItem.municipio?.estado?.sigla || 'BR'})
                    </strong>
                  </div>

                  <div>
                    <span style={{ fontSize: '.72rem', color: '#64748b', display: 'block' }}>Município:</span>
                    <strong style={{ fontSize: '.84rem', color: '#0f172a' }}>
                      {detailItem.escola?.municipio?.nome || detailItem.municipio?.nome || 'Não vinculado'}
                    </strong>
                  </div>

                  <div>
                    <span style={{ fontSize: '.72rem', color: '#64748b', display: 'block' }}>Escola / Órgão:</span>
                    <strong style={{ fontSize: '.84rem', color: '#0f172a' }}>
                      {detailItem.escola?.nome || (detailItem.municipio ? `Secretaria Municipal de ${detailItem.municipio.nome}` : 'Administração Master')}
                    </strong>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 6, borderTop: '1px solid #e2e8f0', fontSize: '.78rem', color: '#64748b' }}>
                  <span><strong>Emissor:</strong> {detailItem.criadoPor?.nome || 'Sistema Automático'} ({detailItem.criadoPor?.email || 'N/A'})</span>
                  <span style={{ marginLeft: 'auto', background: '#e2e8f0', padding: '2px 8px', borderRadius: 4, fontWeight: 600, color: '#334155' }}>
                    {detailItem.criadoPor?.perfil || 'ADMIN'}
                  </span>
                </div>
              </div>

              {/* ── Bloco 2: Classificação, Destinatário e Estatísticas de Alcance ── */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8,
              }}>
                <div style={{ padding: 10, background: '#ffffff', borderRadius: 8, border: '1.5px solid #e2e8f0' }}>
                  <span style={{ fontSize: '.68rem', color: '#64748b', display: 'block' }}>Tipo</span>
                  <span style={{
                    display: 'inline-block', marginTop: 3, padding: '2px 6px', borderRadius: 5, fontSize: '.7rem', fontWeight: 700,
                    background: TIPO_COLORS[detailItem.tipo]?.bg || '#f3f4f6', color: TIPO_COLORS[detailItem.tipo]?.color || '#374151',
                  }}>
                    {detailItem.tipo}
                  </span>
                </div>

                <div style={{ padding: 10, background: '#ffffff', borderRadius: 8, border: '1.5px solid #e2e8f0' }}>
                  <span style={{ fontSize: '.68rem', color: '#64748b', display: 'block' }}>Categoria</span>
                  <strong style={{ fontSize: '.8rem', color: '#0f172a', display: 'block', marginTop: 3 }}>
                    {detailItem.categoria}
                  </strong>
                </div>

                <div style={{ padding: 10, background: '#ffffff', borderRadius: 8, border: '1.5px solid #e2e8f0' }}>
                  <span style={{ fontSize: '.68rem', color: '#64748b', display: 'block' }}>Público Alvo</span>
                  <strong style={{ fontSize: '.8rem', color: '#0f172a', display: 'block', marginTop: 3 }}>
                    {detailItem.destinatario === 'TURMA' ? `Turma ${detailItem.turma?.nome || ''}` : detailItem.destinatario}
                  </strong>
                </div>

                <div style={{ padding: 10, background: '#ffffff', borderRadius: 8, border: '1.5px solid #e2e8f0' }}>
                  <span style={{ fontSize: '.68rem', color: '#64748b', display: 'block' }}>Visualizações</span>
                  <strong style={{ fontSize: '.95rem', color: '#059669', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                    <Eye size={13} /> {detailItem._count?.visualizacoes || 0}
                  </strong>
                </div>

                <div style={{ padding: 10, background: '#ffffff', borderRadius: 8, border: '1.5px solid #e2e8f0' }}>
                  <span style={{ fontSize: '.68rem', color: '#64748b', display: 'block' }}>Alcance Potencial</span>
                  <strong style={{ fontSize: '.85rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                    <Users size={13} />
                    {detailItem.destinatario === 'TURMA'
                      ? `${detailItem.turma?._count?.alunos || 0} alunos`
                      : detailItem.escola
                      ? `${detailItem.escola._count?.alunos || 0} alunos`
                      : 'Toda a rede'}
                  </strong>
                </div>
              </div>

              {/* ── Bloco 3: Mensagem Completa Transmitida ── */}
              <div>
                <label style={{ fontWeight: 700, fontSize: '.84rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <FileText size={14} style={{ color: '#2563eb' }} /> Conteúdo Integral Transmitido:
                </label>
                <div style={{
                  padding: 14, background: '#ffffff', borderRadius: 8,
                  border: '1.5px solid #cbd5e1', fontSize: '.88rem',
                  color: '#0f172a', lineHeight: 1.55, whiteSpace: 'pre-wrap', minHeight: 90,
                }}>
                  {detailItem.corpo}
                </div>
              </div>
            </div>

            {/* ═══ COLUNA DIREITA: PAINEL DE MÍDIA ANEXA (QUANDO HOUVER) ═══ */}
            {hasDetailMedia && (
              <div style={{
                flex: '0.9 1 340px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                padding: 14,
                background: '#f8fafc',
                borderRadius: 10,
                border: '1.5px solid #cbd5e1',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '.78rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ImageIcon size={14} style={{ color: '#2563eb' }} /> Mídia / Anexo Associado
                  </span>
                </div>

                {/* Preview Visual da Imagem / Vídeo */}
                <div style={{
                  width: '100%',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 240,
                  maxHeight: 360,
                  border: '1px solid #e2e8f0',
                }}>
                  {isImage ? (
                    <img
                      src={mediaUrl}
                      alt={detailItem.configPreview?.midiaTitulo || detailItem.titulo}
                      style={{
                        maxWidth: '100%',
                        maxHeight: 360,
                        objectFit: 'contain',
                        display: 'block',
                      }}
                      onError={(e) => {
                        // Fallback em caso de URL inválida
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  ) : isVideo ? (
                    <video
                      src={mediaUrl}
                      controls
                      style={{ maxWidth: '100%', maxHeight: 360 }}
                    />
                  ) : (
                    <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                      <FileText size={40} style={{ margin: '0 auto 8px', opacity: 0.7 }} />
                      <div style={{ fontSize: '.84rem', color: '#ffffff', fontWeight: 600 }}>
                        {detailItem.configPreview?.midiaTitulo || 'Documento Anexo'}
                      </div>
                      <span style={{ fontSize: '.75rem' }}>Clique no botão acima para visualizar</span>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: '.75rem', color: '#64748b', lineHeight: 1.4 }}>
                  <strong>Título da Mídia:</strong> {detailItem.configPreview?.midiaTitulo || 'Anexo padrão'}<br />
                  <span style={{ color: '#059669', fontWeight: 600 }}>✓ Anexado via Banco de Mídias Institucional</span>
                </div>
              </div>
            )}
          </div>

          {/* Rodapé do Modal */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, marginTop: 14, borderTop: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={12} /> Data do Envio: {formatDate(detailItem.criadoEm)}
            </span>
            <button className="btn-cancel" onClick={() => setDetailItem(null)}>
              Fechar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
